/**
 * STORY — all copy lives here.
 * Edit anything in this file to change the site's words; components read from it.
 * Numbers in `ticker` are mock data, hardcoded on purpose, gently animated in the UI.
 * Lines marked "IMAGE SLOT" are placeholders waiting for real screenshots/art.
 */

export const nav = {
  brand: "Story",
  links: [
    { label: "Worlds", href: "#worlds" },
    { label: "Life inside", href: "#town" },
    { label: "Ways in", href: "#windows" },
    { label: "FAQ", href: "#faq" },
  ],
  login: { label: "Log in with Discord", href: "#" }, // plain link on purpose — no OAuth on the landing page
};

export const hero = {
  headline: "Worlds that remember you.",
  sub: "Living worlds where every character has a life, a memory and an opinion of you. An AI Dungeon Master is always running the game. Step in from your browser or from Minecraft.",
  ctas: {
    primary: "Play now", // opens browser play
    secondary: "Get the launcher", // 3D in Minecraft
    tertiary: "Join the Discord",
  },
  // IMAGE SLOT: replace src with the painterly dusk-town hero art (keep the alt).
  art: {
    src: "/img/hero-town.png",
    alt: "A lantern-lit town at dusk: crooked rooftops, glowing windows, warm smoke drifting over a cobbled square.",
  },
};

export const ticker = {
  label: "Live now",
  // Mock numbers — hardcode here, gently animated where they're shown.
  players: 1284,
  worlds: 7,
};

export const worlds = {
  kicker: "Pick a world",
  heading: "Campaigns, not servers",
  body: "Every world has its own lore, its own factions, its own history. Pick one and step in — from your browser or in 3D.",
  note: "N playing now — the town notices when someone new arrives.",
  cards: [
    {
      title: "Emberhollow",
      tagline: "A mining town that keeps a secret under its streets.",
      tags: ["Low fantasy", "Mystery", "Town life"],
      playing: 312,
      windows: ["web", "3D"] as const,
      // IMAGE SLOT: replace with real campaign cover art.
      cover: "/img/campaign-emberhollow.png",
      coverAlt: "Cover art: a lantern-lit mining town built into a dark hillside.",
    },
    {
      title: "Saltwrack",
      tagline: "A smuggler's port where every tide brings a new debt.",
      tags: ["Sea adventure", "Politics", "Survival"],
      playing: 507,
      windows: ["web", "3D"] as const,
      cover: "/img/campaign-saltwrack.png",
      coverAlt: "Cover art: a storm-grey harbour town under torn sails and a low moon.",
    },
    {
      title: "Vellum",
      tagline: "A library-city where forgotten books wake up.",
      tags: ["Whimsy", "Magic", "Cozy"],
      playing: 465,
      windows: ["web"] as const,
      cover: "/img/campaign-vellum.png",
      coverAlt: "Cover art: candlelit library towers stacked with glowing books.",
    },
  ],
};

export const town = {
  id: "town",
  kicker: "A town that keeps living",
  heading: "The world doesn't wait for you to log in",
  body: "People in Story have jobs, shifts, hunger and grudges. They gossip, trade, get paid, slack off — and remember what you did to them. This is one night in Emberhollow.",
  offlineLabel: "This happened while you were offline.",
  npc: {
    name: "Maren",
    job: "Night watchman",
    mood: "Weary, but fond of you",
    // The one memory line shown on her NPC card.
    memory: "Remembers you stole bread on Tuesday.",
    // IMAGE SLOT: replace with a crisp 128px pixel-art portrait.
    portrait: "/img/npc-maren.png",
    portraitAlt: "Pixel-art portrait of Maren: a tired watchwoman with kind eyes and a wool hood.",
  },
  timeline: [
    { time: "17:40", text: "Wakes up at dusk, grumbling. Puts on the watch coat. It still smells like the cellar you hid in." },
    { time: "19:05", text: "Clocks in at the gate. Waves off Kessa the lamp-lighter — they argued about a debt last week and neither has let go." },
    { time: "21:30", text: "Buys a meat pie from Tomas's cart. Doesn't mention that Tomas owes the Watch a favour. Files it away." },
    { time: "23:15", text: "Catches a boy skimming the till at the storehouse. Lets him go with a warning. He'll be back tomorrow." },
    { time: "01:50", text: "Walks the north wall. Thinks about the traveller who stole bread on Tuesday. Plans to say something." },
  ],
};

export const dm = {
  kicker: "Your Dungeon Master never sleeps",
  heading: "Someone is always running the game",
  body: "While you play, the Dungeon Master watches what you do, sets up consequences, hands out quests and narrates like a storyteller sitting across the table.",
  narration: [
    "You feel the guard's eyes on your back.",
    "The baker's shutters are closed. Someone has nailed a notice to the door — and your name is on it.",
    "Rain starts. Somewhere below, a dog starts barking, and doesn't stop.",
  ],
  quest: {
    toast: "New quest",
    title: "Return the ledger to the Watch House",
    detail: "Bring it before dawn and Maren may forget Tuesday. Mostly.",
  },
};

export const windows = {
  id: "windows",
  kicker: "One world, many windows",
  heading: "Same world. Same people. Pick your window.",
  body: "A browser player and a Minecraft player can stand in the same square, talk to the same person, be remembered by the same name.",
  webCard: {
    title: "In your browser",
    body: "Play instantly — no install, no account juggling. Parchment, ink and candlelight.",
    // IMAGE SLOT: replace with a real screenshot of the parchment-style web view.
    shot: "/img/window-web.png",
    shotAlt: "The same tavern scene shown in the web view: ink-and-parchment illustration of Maren behind a bar.",
  },
  blockCard: {
    title: "In Minecraft",
    body: "Walk the world in 3D through the Story launcher. Same streets, same people, now under your feet.",
    // IMAGE SLOT: replace with a real screenshot of the 3D view through the launcher.
    shot: "/img/window-3d.png",
    shotAlt: "The same tavern scene shown in 3D: a blocky lantern-lit interior with Maren behind the bar.",
  },
};

export const voice = {
  kicker: "Speak and be heard",
  heading: "Type it or say it out loud",
  body: "Talk into your mic and people hear you, near or far. Characters answer in their own voice — every one of them.",
  exchange: {
    you: "Maren — about that bread. I'd like to make it right.",
    reply: "\"About time,\" she says. \"Two loaves, fresh from the morning batch, and you help Tomas stack his cart. Then we're even.\"",
  },
};

export const death = {
  kicker: "Death is a chapter",
  heading: "When you fall, the story doesn't end",
  body: "Your soul crosses over, meets a guide, and begins a new life — in a world that still remembers the old one. The debts, the friends, the reputation: they carry over.",
  guideLine: "\"You again,\" says the guide, not unkindly. \"We should stop meeting like this.\"",
  cta: "Begin a new life",
};

export const steps = {
  kicker: "Getting started",
  heading: "In three steps",
  items: [
    {
      n: "1",
      title: "Log in with Discord",
      body: "One identity across every way in — browser, launcher, whatever comes next.",
    },
    {
      n: "2",
      title: "Pick a world",
      body: "A mining town, a smuggler's port, a library that wakes up. Each one is a campaign.",
    },
    {
      n: "3",
      title: "Play in the browser or launch in 3D",
      body: "Step in from wherever you are. The world's already running.",
    },
  ],
};

export const faq = {
  kicker: "Questions",
  heading: "Before you step in",
  items: [
    {
      q: "Is it free?",
      a: "Yes. You can play every world for free. Some worlds may offer extra things to buy later, but the game itself doesn't cost anything.",
    },
    {
      q: "Do I need Minecraft?",
      a: "No. It's one way in. The browser works on its own — Minecraft is just a different window on the same world.",
    },
    {
      q: "Can my friends and I play on different devices together?",
      a: "Yes. One of you in the browser, one in Minecraft, one on a laptop with a potato for a GPU — you all meet in the same world, same town, same story.",
    },
    {
      q: "Is it safe for teens?",
      a: "Story is built for players 14 and up. Chats are moderated, voice has distance limits, and world owners can set their own house rules.",
    },
    {
      q: "Can I make my own world?",
      a: "Coming soon. Story is becoming a platform for creators — join the waitlist below to hear when the gates open.",
    },
  ],
};

export const footer = {
  blurb: "Roblox, but every world is a story.",
  links: {
    discord: "Join the Discord",
    launcher: "Get the launcher",
    creators: "For creators (coming soon)",
  },
  waitlist: {
    heading: "Be first through the gate",
    body: "Creators get their keys first. Leave an email and we'll knock when it's your turn.",
    placeholder: "you@example.com",
    button: "Join the waitlist",
    success: "You're on the list. We'll knock when it's your turn.",
    error: "That didn't work — try again in a moment.",
    invalid: "That email doesn't look quite right.",
  },
  smallPrint: "© Story. All worlds fictional. Any resemblance to people you've wronged is intentional.",
};
